// apps/web/src/features/admin/components/permissions-vocab.ts

// CHECKPOINT C — admin endpoint migration (#74-followup) — RESOLVED 2026-05-03.
// All `requirePermission("admin")` calls in the API have been migrated to
// per-capability tenant vocabulary (manage-roles, manage-users, etc.). The
// design doc anticipated ~30 endpoints; the actual scope was 9 calls in one
// file (apps/api/src/routes/roles/index.ts). Migration coverage verified by
// the "admin vocabulary granularity" describe block in roles.test.ts.

export const FIELD_ABILITIES = [
  { id: 'view',          label: 'View',     family: 'field' as const },
  { id: 'edit',          label: 'Edit',     family: 'field' as const },
  { id: 'complete',      label: 'Complete', family: 'field' as const },
  { id: 'override',      label: 'Override', family: 'field' as const },
  { id: 'escalate-from', label: 'Escalate', family: 'field' as const },
  { id: 'reject',        label: 'Reject',   family: 'field' as const },
] as const;

export const STRUCTURAL_ABILITIES = [
  { id: 'split-doc',       label: 'Split document',     family: 'structural' as const },
  { id: 'merge-docs',      label: 'Merge documents',    family: 'structural' as const },
  { id: 'reorder-pages',   label: 'Reorder pages',      family: 'structural' as const },
  { id: 'reclassify-doc',  label: 'Reclassify',         family: 'structural' as const },
  { id: 'delete-doc',      label: 'Delete document',    family: 'structural' as const },
  { id: 'edit-batch-meta', label: 'Edit batch metadata', family: 'structural' as const },
] as const;

export const TENANT_CAPABILITIES = [
  { id: 'manage-users',          label: 'Manage users' },
  { id: 'manage-roles',          label: 'Manage roles' },
  { id: 'manage-job-types',      label: 'Manage job types' },
  { id: 'manage-input-channels', label: 'Manage input channels' },
  { id: 'manage-export-formats', label: 'Manage export formats' },
  { id: 'manage-rules',          label: 'Manage rules' },
  { id: 'view-analytics',        label: 'View analytics' },
  { id: 'view-reports',          label: 'View reports' },
  { id: 'monitor-live',          label: 'Monitor (live ops)' },
  { id: 'monitor-intervene',     label: 'Monitor: intervene' },
  { id: 'tenant-settings',       label: 'Tenant settings' },
  { id: 'admin-ocr-dashboard',   label: 'View OCR feedback dashboard' },
  // R-162 §10.1 — dot-key IS the id IS the stored action IS the session key
  // (no translation layer). Surfaced in RoleEditor's tenant-capability checklist.
  { id: 'supervisor.queue_monitor', label: 'Monitor queues (roster)' },
  { id: 'supervisor.queue_block',   label: 'Block queues' },
  // WU-2.1 — dot-key IS the id IS the stored action IS the session key.
  { id: 'supervisor.announce',      label: 'Post announcements' },
  // WU-2.2 — per-queue standing instructions.
  { id: 'supervisor.queue_instruct', label: 'Set queue instructions' },
  // WU-2.3 — directed notes.
  { id: 'supervisor.direct_note', label: 'Send directed notes' },
  // WU-2.4 — queue evictions.
  { id: 'supervisor.queue_evict', label: 'Evict operators from queues' },
  // T1 — team management.
  { id: 'team.manage', label: 'Manage teams' },
  // SP1 remittance foundation §4 — dot-key IS the id IS the stored action IS
  // the session key. Defined + granted in SP1; consumed by SP3 stations.
  { id: 'remittance.key',     label: 'Key remittance transactions' },
  { id: 'remittance.balance', label: 'Balance remittance transactions' },
] as const;

export const GRANTABLE_STAGES = [
  { id: '*',             label: 'All' },
  { id: 'import',        label: 'Import' },
  { id: 'batch-fix',     label: 'Batch Fix' },
  { id: 'post-ocr',      label: 'Post-OCR' },
  // R-261 5d-5a — the classify station is a grantable human stage (Supervisor
  // holds complete/reclassify-doc/delete-doc here via the system-role respec).
  { id: 'classify',      label: 'Classify' },
  { id: 'key-entry',     label: 'Key' },
  { id: 'verification',  label: 'Verify' },
  { id: 'qa',            label: 'QA' },
  { id: 'supervisor',    label: 'Supervisor' },
  { id: 'pre-export',    label: 'Pre-Export' },
  { id: 'escalation',    label: 'Escalation' },
] as const;

export type FieldAbility = typeof FIELD_ABILITIES[number]['id'];
export type StructuralAbility = typeof STRUCTURAL_ABILITIES[number]['id'];
export type TenantCapability = typeof TENANT_CAPABILITIES[number]['id'];
export type GrantableStage = typeof GRANTABLE_STAGES[number]['id'];
export type Action = FieldAbility | StructuralAbility | TenantCapability;

const FIELD_ABILITY_IDS = new Set<string>(FIELD_ABILITIES.map((a) => a.id));
const STRUCTURAL_ABILITY_IDS = new Set<string>(STRUCTURAL_ABILITIES.map((a) => a.id));
const TENANT_CAPABILITY_IDS = new Set<string>(TENANT_CAPABILITIES.map((a) => a.id));

export const isFieldAbility = (a: string): a is FieldAbility => FIELD_ABILITY_IDS.has(a);
export const isStructuralAbility = (a: string): a is StructuralAbility => STRUCTURAL_ABILITY_IDS.has(a);
export const isTenantCapability = (a: string): a is TenantCapability => TENANT_CAPABILITY_IDS.has(a);

export const ALL_ACTIONS: readonly Action[] = [
  ...FIELD_ABILITIES.map((a) => a.id),
  ...STRUCTURAL_ABILITIES.map((a) => a.id),
  ...TENANT_CAPABILITIES.map((a) => a.id),
] as readonly Action[];

export const ALL_STAGES: readonly GrantableStage[] = GRANTABLE_STAGES.map((s) => s.id) as readonly GrantableStage[];
